"use client";

import type React from "react";
import { useState, useEffect } from "react";
import {
  useCurrentRefinements,
  useRefinementList,
} from "react-instantsearch";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  X,
  Filter,
} from "lucide-react";
import { BudgetFilter } from "@/customcomponents/ui/customs/budget_filter";


// Custom filter list component with show all functionality
function CustomFilterList({ attribute, ...props }: { attribute: string }) {
  const [showAll, setShowAll] = useState(false);
  const maxItems = 4;
  
  const { items, refine } = useRefinementList({
    attribute,
    limit: 100, // Get all items
    ...props
  });

  const displayItems = showAll ? items : items.slice(0, maxItems);

  return (
    <div className="space-y-3">
      <div className={showAll ? "space-y-2 max-h-48 overflow-y-auto pr-2" : "space-y-2"}>
        {displayItems.map((item) => (
          <div
            key={item.label}
            className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-50 cursor-pointer transition-all duration-200"
            onClick={() => refine(item.value)}
          >
            <div className="flex items-center space-x-2 flex-1">
              <Checkbox
                checked={item.isRefined}
                onChange={() => {}}
                className="h-4 w-4 rounded text-blue-600 focus:ring-blue-500 focus:ring-2 hover:before:content-['✓'] hover:before:absolute hover:before:text-slate-300 hover:before:text-xs hover:before:font-light hover:before:opacity-50 relative"
              />
              <Label className="text-base text-slate-900 font-normal flex-1 cursor-pointer">
                {item.label}
              </Label>
            </div>
            <span className="text-xs text-slate-500 font-normal">
              {item.count}
            </span>
          </div>
        ))}
      </div>
      
      {/* Show All Button */}
      {items.length > maxItems && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowAll(!showAll)}
          className="w-full text-blue-600 hover:text-blue-700 hover:bg-blue-50 font-medium text-sm"
        >
          {showAll ? "Show Less" : "Show All"}
        </Button>
      )}
    </div>
  );
}

// Follower Range Filter component for a specific social media platform
function FollowerRangeFilter({
  platform,
  attribute,
}: {
  platform: string;
  attribute: string;
}) {
  const { start, range, refine } = useRange({
    attribute,
  });

  // Check if range values are loaded from Meilisearch
  const isRangeLoaded = range.min !== undefined && range.min !== null && 
                        range.max !== undefined && range.max !== null;
  
  // Use actual min/max from Meilisearch, or fallback if not loaded yet
  const minBound = isRangeLoaded ? range.min : 0;
  const maxBound = isRangeLoaded ? range.max : 1000000;

  // Get current min and max values from refinement, or use bounds as defaults
  const currentMin = start[0] !== undefined && start[0] !== null ? start[0] : minBound;
  const currentMax = start[1] !== undefined && start[1] !== null ? start[1] : maxBound;

  const [minValue, setMinValue] = useState<string>((currentMin ?? minBound ?? 0).toString());
  const [maxValue, setMaxValue] = useState<string>((currentMax ?? maxBound ?? 1000000).toString());
  const [hasInitialized, setHasInitialized] = useState(false);
  const [isEditingMin, setIsEditingMin] = useState(false);
  const [isEditingMax, setIsEditingMax] = useState(false);

  // Initialize with actual range values from Meilisearch when they're loaded
  useEffect(() => {
    if (isRangeLoaded && !hasInitialized && minBound !== undefined && maxBound !== undefined) {
      // If no refinement is set, apply actual min/max from Meilisearch
      const needsDefault = start[0] === undefined && start[1] === undefined;
      if (needsDefault) {
        refine([minBound, maxBound]);
        setMinValue(minBound.toString());
        setMaxValue(maxBound.toString());
      }
      setHasInitialized(true);
    }
  }, [isRangeLoaded, hasInitialized, start, refine, minBound, maxBound]);

  // Update local state when refinement changes (but not during user input)
  useEffect(() => {
    if (hasInitialized && isRangeLoaded && minBound !== undefined && maxBound !== undefined) {
      // Don't update if user is currently editing
      if (isEditingMin || isEditingMax) return;
      
      const newMin = (currentMin ?? minBound).toString();
      const newMax = (currentMax ?? maxBound).toString();
      // Only update if values actually changed to avoid overwriting user input
      if (newMin !== minValue || newMax !== maxValue) {
        // Check if this is a real change (not just initialization)
        if (start[0] !== undefined || start[1] !== undefined) {
          setMinValue(newMin);
          setMaxValue(newMax);
        }
      }
    }
  }, [currentMin, currentMax, hasInitialized, isRangeLoaded, minValue, maxValue, start, minBound, maxBound, isEditingMin, isEditingMax]);

  const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!isRangeLoaded || minBound === undefined || maxBound === undefined) return;
    
    const value = e.target.value;
    setIsEditingMin(true);
    setMinValue(value);
    
    if (value === "") {
      // Don't refine if empty, wait for blur
      return;
    }
    
    const numValue = Number(value);
    if (isNaN(numValue)) return;
    
    // Use current max value, or actual max from Meilisearch if current is default
    const max = (start[1] !== undefined) ? (currentMax ?? maxBound) : maxBound;
    
    refine([numValue, max]);
  };

  const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!isRangeLoaded || minBound === undefined || maxBound === undefined) return;
    
    const value = e.target.value;
    setIsEditingMax(true);
    setMaxValue(value);
    
    if (value === "") {
      // Don't refine if empty, wait for blur
      return;
    }
    
    const numValue = Number(value);
    if (isNaN(numValue)) return;
    
    // Use current min value, or actual min from Meilisearch if current is default
    const min = (start[0] !== undefined) ? (currentMin ?? minBound) : minBound;
    
    refine([min, numValue]);
  };

  const handleMinFocus = () => {
    setIsEditingMin(true);
  };

  const handleMaxFocus = () => {
    setIsEditingMax(true);
  };

  const handleBlur = () => {
    if (!isRangeLoaded || minBound === undefined || maxBound === undefined) return;
    
    setIsEditingMin(false);
    setIsEditingMax(false);
    
    // Ensure values are within bounds and min <= max
    let min = Number(minValue) || minBound;
    let max = Number(maxValue) || maxBound;
    
    if (min < minBound) min = minBound;
    if (max > maxBound) max = maxBound;
    if (min > max) {
      const temp = min;
      min = max;
      max = temp;
    }
    
    setMinValue(min.toString());
    setMaxValue(max.toString());
    refine([min, max]);
  };

  // Show loading state while range values are being loaded
  if (!isRangeLoaded) {
    return (
      <div className="mt-3 pl-6 space-y-3">
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <Label htmlFor={`${platform}-min`} className="text-xs text-slate-600 mb-1.5 block">
              Min
            </Label>
            <div className="h-9 bg-slate-100 rounded-lg animate-pulse" />
          </div>
          <div className="flex-1">
            <Label htmlFor={`${platform}-max`} className="text-xs text-slate-600 mb-1.5 block">
              Max
            </Label>
            <div className="h-9 bg-slate-100 rounded-lg animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-3 pl-6 space-y-3">
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <Label htmlFor={`${platform}-min`} className="text-xs text-slate-600 mb-1.5 block">
            Min
          </Label>
          <Input
            id={`${platform}-min`}
            type="number"
            value={minValue}
            onChange={handleMinChange}
            onFocus={handleMinFocus}
            onBlur={handleBlur}
            min={minBound}
            max={maxBound}
            placeholder={minBound?.toString() ?? "0"}
            className="w-full"
          />
        </div>
        <div className="flex-1">
          <Label htmlFor={`${platform}-max`} className="text-xs text-slate-600 mb-1.5 block">
            Max
          </Label>
          <Input
            id={`${platform}-max`}
            type="number"
            value={maxValue}
            onChange={handleMaxChange}
            onFocus={handleMaxFocus}
            onBlur={handleBlur}
            min={minBound}
            max={maxBound}
            placeholder={maxBound?.toString() ?? "1000000"}
            className="w-full"
          />
        </div>
      </div>
    </div>
  );
}

// Social media toggle with count display and follower range filter
function SocialMediaToggle({
  attribute,
  label,
  platform,
}: {
  attribute: string;
  label: string;
  platform: string;
}) {
  const { items, refine } = useRefinementList({
    attribute,
    limit: 100,
  });

  // Find the item that matches "true" (has this platform)
  // Meilisearch may store boolean values as strings or actual booleans
  const platformItem = items.find(item => {
    const value = item.value;
    return value === "true" || value === "True" || String(value) === "true";
  });
  const isRefined = platformItem?.isRefined || false;
  const count = platformItem?.count || 0;

  // Map platform names to Meilisearch attribute names for follower counts
  const followerAttributeMap: Record<string, string> = {
    youtube: "socialStats.youtube",
    instagram: "socialStats.instagram",
    twitter: "socialStats.twitter",
    facebook: "socialStats.facebook",
    tiktok: "socialStats.tiktok",
    twitch: "socialStats.twitch",
  };

  const followerAttribute = followerAttributeMap[platform.toLowerCase()];

  const handleClick = () => {
    if (platformItem) {
      refine(platformItem.value);
    } else if (items.length > 0) {
      // Fallback: try to refine with "true" string
      refine("true");
    }
  };

  return (
    <div>
      <div
        className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-50 cursor-pointer transition-all duration-200"
        onClick={handleClick}
      >
        <div className="flex items-center space-x-2 flex-1">
          <Checkbox
            checked={isRefined}
            onChange={() => {}}
            className="h-4 w-4 rounded text-blue-600 focus:ring-blue-500 focus:ring-2 hover:before:content-['✓'] hover:before:absolute hover:before:text-slate-300 hover:before:text-xs hover:before:font-light hover:before:opacity-50 relative"
          />
          <Label className="text-base text-slate-900 font-normal flex-1 cursor-pointer capitalize">
            {label}
          </Label>
        </div>
        <span className="text-xs text-slate-500 font-normal">
          {count}
        </span>
      </div>
      {/* Show follower range filter when platform is selected */}
      {isRefined && followerAttribute && (
        <FollowerRangeFilter
          platform={platform}
          attribute={followerAttribute}
        />
      )}
    </div>
  );
}

// Social Media Filter component matching campaign style
function SocialMediaFilter() {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-3">
        <h3 className="text-xs font-normal text-slate-500 uppercase tracking-wide">Social Media Platforms</h3>
      </div>
      <div className="space-y-2">
        <SocialMediaToggle
          attribute="hasSocialMedia.youtube"
          label="YouTube"
          platform="youtube"
        />
        <SocialMediaToggle
          attribute="hasSocialMedia.instagram"
          label="Instagram"
          platform="instagram"
        />
        <SocialMediaToggle
          attribute="hasSocialMedia.twitter"
          label="Twitter"
          platform="twitter"
        />
        <SocialMediaToggle
          attribute="hasSocialMedia.facebook"
          label="Facebook"
          platform="facebook"
        />
        <SocialMediaToggle
          attribute="hasSocialMedia.tiktok"
          label="TikTok"
          platform="tiktok"
        />
        <SocialMediaToggle
          attribute="hasSocialMedia.twitch"
          label="Twitch"
          platform="twitch"
        />
      </div>
    </div>
  );
}

// Price Range Filter component - now using shared BudgetFilter
function PriceRangeFilter() {
  return <BudgetFilter attribute="avg_price" />;
}

// Filter section matching campaign style (no icons)
function FilterSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-3">
        <h3 className="text-xs font-normal text-slate-500 uppercase tracking-wide">{title}</h3>
      </div>
      <div>
        {children}
      </div>
    </div>
  );
}

// Active Filters component with improved styling
function ActiveFilters() {
  const { items, refine } = useCurrentRefinements();

  if (items.length === 0) {
    return null;
  }

  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-3">
        <Filter className="h-4 w-4 text-blue-600" />
        <span className="text-sm font-semibold text-slate-900">
          Active Filters
        </span>
        <Badge variant="soft" className="text-xs">
          {items.reduce((total, item) => total + item.refinements.length, 0)}
        </Badge>
      </div>
      <div className="flex flex-wrap gap-2">
        {items.map((item) =>
          item.refinements.map((refinement) => (
            <Badge
              key={`${item.attribute}-${refinement.label}`}
              variant="outline"
              className="flex items-center gap-2 px-3 py-1 bg-blue-50 border-blue-200 text-blue-800 hover:bg-blue-100"
            >
              <span className="text-xs font-medium">
                {item.label}: {refinement.label}
              </span>
              <button
                type="button"
                onClick={() => refine(refinement)}
                className="rounded-full p-0.5 hover:bg-blue-200 transition-colors"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))
        )}
      </div>
    </div>
  );
}

// Clear All Button component
function ClearAllButton() {
  const { items, refine } = useCurrentRefinements();

  const handleClearAll = () => {
    items.forEach((item) => {
      item.refinements.forEach((refinement) => {
        refine(refinement);
      });
    });
  };

  return (
    <div className="pt-2">
      <Button
        variant="soft"
        size="sm"
        onClick={handleClearAll}
        className="w-full"
      >
        Clear All Filters
      </Button>
    </div>
  );
}

export function SearchFilters() {
  return (
    <div className="h-full flex flex-col">
      <ActiveFilters />

      <ScrollArea className="flex-1 pr-4">
        <div className="space-y-6 pb-4">
          {/* Social Media Platforms */}
          <SocialMediaFilter />
          
          <Separator />

          {/* Price Range */}
          <PriceRangeFilter />
          
          <Separator />

          {/* Categories */}
          <FilterSection title="Categories">
            <CustomFilterList attribute="category" />
          </FilterSection>
          
          <Separator />

          {/* Country */}
          <FilterSection title="Countries">
            <CustomFilterList attribute="country" />
          </FilterSection>
          
          <Separator />

          {/* Languages */}
          <FilterSection title="Languages">
            <CustomFilterList attribute="language" />
          </FilterSection>
          
          <Separator />

          {/* Labels */}
          <FilterSection title="Labels">
            <CustomFilterList attribute="label" />
          </FilterSection>

          <Separator />
          
          {/* Clear All Button */}
          <ClearAllButton />
        </div>
      </ScrollArea>
    </div>
  );
}
